import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ArrowRight } from "lucide-react";
import { fetchSuburbs } from "../../api/public.api.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

const REGIONS = [
  {
    name: "Northern suburbs",
    blurb: "Wollert, Mickleham, Donnybrook — high-growth corridors where we have current builds.",
    suburbs: ["Wollert", "Mickleham", "Donnybrook", "Craigieburn", "Mernda"],
  },
  {
    name: "Eastern suburbs",
    blurb: "Glen Waverley, Wheelers Hill, Mount Waverley — knockdown-rebuild specialists.",
    suburbs: ["Glen Waverley", "Mount Waverley", "Wheelers Hill", "Vermont", "Box Hill"],
  },
  {
    name: "Western corridor",
    blurb: "Truganina, Tarneit, Werribee — first-home and investor builds.",
    suburbs: ["Truganina", "Tarneit", "Werribee", "Point Cook"],
  },
  {
    name: "Regional Victoria",
    blurb: "Lucas, Ballarat, Bendigo — the right project in the right town, we travel for it.",
    suburbs: ["Lucas", "Ballarat", "Bendigo", "Geelong"],
  },
];

export default function WhereWeBuildPage() {
  const { data: suburbs = [] } = useQuery({
    queryKey: ["public", "suburbs"],
    queryFn: fetchSuburbs,
    staleTime: 10 * 60_000,
  });

  return (
    <>
      <SEO
        title="Where We Build"
        description="Greater Melbourne and regional Victoria — the suburbs we currently build in."
      />

      <PageHero
        eyebrow="Service area"
        title="Where we build"
        description="Greater Melbourne and regional Victoria. Don't see your suburb? Get in touch — we travel for the right project."
      />

      <Container className="py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {REGIONS.map((r) => (
            <article
              key={r.name}
              className="rounded-2xl border border-border-subtle bg-bg-elevated p-6"
            >
              <div className="flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-accent" />
                <div>
                  <h2 className="font-display text-xl text-text-primary">{r.name}</h2>
                  <p className="mt-1 text-sm text-text-secondary">{r.blurb}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {r.suburbs.map((s) => (
                  <span
                    key={s}
                    className="rounded-full border border-border-subtle bg-bg-elevated-2 px-3 py-1 text-xs text-text-secondary"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        {suburbs.length > 0 && (
          <section className="mt-16 rounded-2xl border border-border-subtle bg-bg-elevated p-8 lg:p-10">
            <p className="eyebrow">Active house &amp; land suburbs</p>
            <h2 className="mt-3 font-display text-2xl text-text-primary">
              Suburbs with packages available right now
            </h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {suburbs.map((s) => (
                <Link
                  key={s}
                  to={`/house-land?suburb=${encodeURIComponent(s)}`}
                  className="rounded-full border border-border-subtle bg-bg-elevated-2 px-3 py-1 text-xs text-text-primary hover:border-accent hover:text-accent"
                >
                  {s}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border-subtle bg-bg-elevated p-8">
          <div>
            <h3 className="font-display text-2xl text-text-primary">Suburb not on the list?</h3>
            <p className="mt-1 text-sm text-text-secondary">
              We regularly take on builds outside our standard footprint when the project is right.
            </p>
          </div>
          <Link to="/contact-us" className="btn-primary">
            Get in touch
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </>
  );
}
